import React, { ReactNode, useCallback } from 'react';
import { FormRenderProps, Form as ReactForm } from 'react-final-form';
import { Link as RouterLink } from 'react-router-dom';

import { Config } from 'final-form';

import { Link, Text } from '@nord/ui';

import SuccessAlert from '../../../../molecules/Alert/Success';
import { FormError } from '../../../../molecules/Form/FormError';
import PasswordField from '../../../../molecules/Form/PasswordField';
import Submit from '../../../../molecules/Form/Submit';
import { isSubmitDisabled } from '../../../../tools/isSubmitDisabled';
import { passwordValidation } from '../../../../validation';
import {
  isPasswordSecure,
  repeatedPasswordValidation,
} from '../../../../validation/PasswordValidation';

export interface IResetPasswordProps {
  isSubmitting?: boolean;
}

export interface IResetPasswordValues {
  password: string;
  repeat_password: string;
}

export interface IResetPasswordFormText {
  submitSucceededAlert?: ReactNode;
  backToLoginText?: ReactNode;
  formHeader?: ReactNode;
  formSecurePasswordAlert?: ReactNode;
  resetPasswordButtonText?: ReactNode;
  newPasswordLabel?: string;
  repeatPasswordLabel?: string;
}

const ResetPasswordForm = ({
  isSubmitting,
  onSubmit,
  ...restProps
}: Config<IResetPasswordValues> & IResetPasswordFormText & IResetPasswordProps) => {
  const {
    submitSucceededAlert = 'Password has been reset. You can now log in.',
    backToLoginText = 'Back to Login',
    // eslint-disable-next-line max-len
    formHeader = 'Almost done. Enter your new password, and from now on you will be able to use it connecting to the application on your devices.',
    formSecurePasswordAlert = 'Your password is secure',
    newPasswordLabel = 'New password',
    repeatPasswordLabel = 'Repeat password',
    resetPasswordButtonText = 'Reset password',
  } = restProps;

  const validate = useCallback(
    ({ password, repeat_password: repeatPassword }: IResetPasswordValues) => ({
      ...passwordValidation(password, 'password'),
      ...repeatedPasswordValidation(password, repeatPassword, 'repeat_password'),
    }),
    []
  );

  const content = useCallback(
    ({
      handleSubmit,
      submitSucceeded,
      values,
      ...restFormProps
    }: FormRenderProps<IResetPasswordValues>) =>
      submitSucceeded ? (
        <>
          <SuccessAlert className="reset-password-success-alert" data-testid="reset-success-alert">
            {submitSucceededAlert}
          </SuccessAlert>
          <Text variant="body2" className="font-medium mt-4">
            <Link
              data-testid="back-to-login"
              component={RouterLink}
              colorClassName="text-blue"
              to=""
            >
              {backToLoginText}
            </Link>
          </Text>
        </>
      ) : (
        <form autoComplete="off" onSubmit={handleSubmit}>
          <div className="reset-password-form-subtitle text-small leading-24 mb-4">
            {formHeader}
          </div>
          <PasswordField id="password-field" name="password" label={newPasswordLabel} required />
          {isPasswordSecure(values.password) && (
            <SuccessAlert data-testid="secure-alert" className="mb-16">
              {formSecurePasswordAlert}
            </SuccessAlert>
          )}
          <PasswordField
            id="password-repeat-field"
            name="repeat_password"
            label={repeatPasswordLabel}
          />
          <Submit
            loading={isSubmitting}
            className="w-full my-3"
            formProps={restFormProps}
            data-testid="submit-reset-password"
            disabled={isSubmitting || isSubmitDisabled(restFormProps)}
          >
            {resetPasswordButtonText}
          </Submit>
          <FormError />
        </form>
      ),
    [
      submitSucceededAlert,
      formHeader,
      formSecurePasswordAlert,
      newPasswordLabel,
      repeatPasswordLabel,
      resetPasswordButtonText,
      backToLoginText,
      isSubmitting,
    ]
  );

  return <ReactForm onSubmit={onSubmit} validate={validate} render={content} />;
};

export default ResetPasswordForm;
